import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useQueryParamsStore } from '@apps/main/new-desktop/core/stores/queryParamsStore';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';

import { getDomain } from '@libs/utils';

import type { PremadeTrip } from '../models';
import { AIService } from '../services/aiService';
import { SanityService } from '../services/sanityService';

export const usePremadeTripStore = defineStore('PremadeTripStore', () => {
  const aiService = new AIService();
  const sanityService = new SanityService();
  const queryParamsStore = useQueryParamsStore();
  const browserStore = useBrowserStore();

  // State
  const premadeTrip = ref<PremadeTrip | undefined>(undefined);

  // Actions
  async function tryToFetchPremadeTripDetails(): Promise<void> {
    tryToFetchPublishedTripDetails();
    tryToFetchAITripDetails();
  }

  async function tryToFetchPublishedTripDetails(): Promise<void> {
    const publishedTripId = queryParamsStore.queryParams?.publishedTripId;

    if (publishedTripId) {
      premadeTrip.value = await sanityService.fetchPublishedTripDetails(
        publishedTripId,
        getDomain.getCurrentDomainSpec().Locale,
        getDomain.getCurrentDomainSpec().FullLocale
      );
      if (premadeTrip.value && premadeTrip.value.headline) {
        browserStore.setPremadeTripTitle(
          `${premadeTrip.value.headline} - Sembo`
        );
      }
    }
  }

  async function tryToFetchAITripDetails(): Promise<void> {
    const aiTripId = queryParamsStore.queryParams?.aiTripId;

    if (!aiTripId) {
      return undefined;
    }

    const fallbackImageUrl =
      (await sanityService.getFallbackAITripImageURL()) ?? '';
    const aiTripWithOptionalImage = await aiService.fetchAITripDetails(
      aiTripId
    );

    if (aiTripWithOptionalImage) {
      premadeTrip.value = {
        ...aiTripWithOptionalImage,
        imageUrl: aiTripWithOptionalImage.imageUrl ?? fallbackImageUrl,
      };
    }
  }

  // Getters
  const showingPremadeTrip = computed<boolean>(() => !!premadeTrip.value);

  return {
    // State
    premadeTrip,

    // Actions
    tryToFetchPremadeTripDetails,

    // Getters
    showingPremadeTrip,
  };
});
